"use client";

import Markdown, { Components } from "react-markdown";
import { SiteLayout } from "./SiteLayout";

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

function Heading({ level, children }: { level: number; children: React.ReactNode }) {
  const text =
    typeof children === "string"
      ? children
      : Array.isArray(children)
        ? children.map((c) => (typeof c === "string" ? c : "")).join("")
        : "";
  const id = slugify(text);
  const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

  return (
    <Tag id={id} className="md-heading">
      <a href={`#${id}`} className="md-heading__anchor" aria-hidden="true" tabIndex={-1}>
        #
      </a>
      {children}
    </Tag>
  );
}

const components: Components = {
  h1: ({ children }) => <Heading level={1}>{children}</Heading>,
  h2: ({ children }) => <Heading level={2}>{children}</Heading>,
  h3: ({ children }) => <Heading level={3}>{children}</Heading>,
  h4: ({ children }) => <Heading level={4}>{children}</Heading>,
};

export function MarkdownPage({ content }: { content: string }) {
  return (
    <SiteLayout>
      <div className="markdown-page">
        <article className="markdown-page__content">
          <Markdown components={components}>{content}</Markdown>
        </article>
      </div>

      <style>{`
        /* Flat, same as the guild pages (--km-blue-page), not the radial
           --site-background wash every hero uses. A gradient reads fine behind
           a few lines of hero copy; stretched the full length of a policy
           document it just looks like a rendering fault. */
        .markdown-page {
          background: var(--km-blue-page, var(--page-background));
          color: var(--foreground);
          padding-top: 48px;
        }

        .markdown-page__content {
          width: min(800px, calc(100vw - 48px));
          margin: 0 auto;
          padding: var(--space-7) 0 var(--space-8);
          font-family: var(--font-inter);
          font-size: 14px;
          line-height: 1.6;
        }

        .markdown-page__content h1 {
          font-family: var(--font-heading);
          font-size: 28px;
          font-weight: 700;
          line-height: 1.15;
          margin: 0 0 var(--space-4);
          color: var(--foreground);
          text-align: center;
        }

        .markdown-page__content h2 {
          font-family: var(--font-heading);
          font-size: 20px;
          font-weight: 700;
          line-height: 1.2;
          margin: var(--space-6) 0 var(--space-3);
          color: var(--foreground);
        }

        .markdown-page__content h3 {
          font-family: var(--font-heading);
          font-size: 17px;
          font-weight: 600;
          line-height: 1.3;
          margin: var(--space-5) 0 var(--space-2);
          color: var(--foreground);
        }

        .markdown-page__content h4 {
          font-family: var(--font-heading);
          font-size: 16px;
          font-weight: 600;
          line-height: 1.3;
          margin: var(--space-5) 0 var(--space-2);
          color: var(--foreground);
        }

        .md-heading {
          position: relative;
        }

        .md-heading__anchor {
          position: absolute;
          left: -1.2em;
          color: var(--muted);
          text-decoration: none;
          font-weight: 400;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .md-heading:hover .md-heading__anchor {
          opacity: 1;
        }

        .md-heading__anchor:hover {
          color: var(--primary);
        }

        .markdown-page__content h1 .md-heading__anchor {
          position: static;
          display: none;
        }

        /* Body copy on these pages is the document, not a caption.
         *
         * THEME.md 12 sets every <p> to --km-text-secondary, which is right for
         * the card descriptions and subtitles it was written for. Here
         * react-markdown emits bare <p> for the whole of a privacy notice, and
         * #8A8078 on the theme background measures 3.74:1 -- under the 4.5:1
         * WCAG AA minimum for body text. --km-text is the theme's own text
         * colour and measures 13.85:1, so this fixes the contrast without
         * inventing a value the theme does not have. Owner's decision. */
        .markdown-page__content p,
        .markdown-page__content li {
          color: var(--foreground);
        }

        .markdown-page__content p {
          margin: 0 0 16px;
        }

        .markdown-page__content a {
          color: var(--primary);
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .markdown-page__content ul {
          margin: 0 0 16px;
          padding-left: 24px;
          list-style-type: disc;
        }

        .markdown-page__content ol {
          margin: 0 0 16px;
          padding-left: 24px;
          list-style-type: decimal;
        }

        .markdown-page__content li {
          margin-bottom: 6px;
        }

        .markdown-page__content li > ul {
          margin-top: 6px;
          margin-bottom: 0;
          list-style-type: circle;
        }

        .markdown-page__content li > ul li {
          color: var(--muted);
        }

        .markdown-page__content li > ol {
          margin-top: 6px;
          margin-bottom: 0;
        }

        .markdown-page__content hr {
          border: none;
          border-top: 2px solid var(--border);
          margin: var(--space-6) 0;
        }

        .markdown-page__content strong {
          font-weight: 700;
        }

        .markdown-page__content em {
          font-style: italic;
        }

        @media (max-width: 640px) {
          .markdown-page__content {
            width: calc(100vw - 32px);
            padding: var(--space-6) 0 var(--space-7);
            font-size: 14px;
          }

          .markdown-page__content h1 {
            font-size: 24px;
          }

          .markdown-page__content h2 {
            font-size: 18px;
            margin-top: var(--space-5);
          }

          .markdown-page__content h3 {
            font-size: 16px;
          }

          .markdown-page__content h4 {
            font-size: 15px;
          }
        }
      `}</style>
    </SiteLayout>
  );
}
